import { z } from "zod";

/**
 * Sprint 2 Accreditation Bundle #3 — Turnaround-Time (TAT) reports.
 *
 * Read-only aggregations computed from the existing `lab_orders.ordered_at`
 * and `lab_orders.completed_at` timestamps. No transactional table is
 * introduced; the only schema addition is `clinic_lab_tests.target_tat_hours`
 * (migration 0070) so the report can flag outliers per test.
 *
 * ISO 15189 §5.6.5 requires labs to monitor TAT as a quality indicator;
 * the per-test breakdown surfaces which tests consistently miss target
 * so the lab manager can act (more reagent, faster instrument, etc).
 */

/** Date-range filter for the TAT report. Defaults to the last 30 days. */
export const tatReportQuerySchema = z.object({
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
});
export type TatReportQuery = z.infer<typeof tatReportQuerySchema>;

/** Per-test aggregate row. */
export const tatStatsRowSchema = z.object({
	test_name: z.string(),
	/** Section/category name when the test belongs to one (e.g. "Hematology"). */
	section: z.string().nullable(),
	/** Number of completed orders containing this test in the window. */
	count: z.number().int(),
	/** Median (p50) TAT in hours. */
	median_hours: z.number(),
	/** 95th percentile TAT in hours. */
	p95_hours: z.number(),
	/** 99th percentile TAT in hours. */
	p99_hours: z.number(),
	min_hours: z.number(),
	max_hours: z.number(),
	/** Echo of clinic_lab_tests.target_tat_hours for this test (null = unset). */
	target_tat_hours: z.number().nullable(),
	/**
	 * When target is set: number of orders where TAT > target.
	 * When target is null: null (we can't classify without a threshold).
	 */
	outlier_count: z.number().int().nullable(),
});
export type TatStatsRow = z.infer<typeof tatStatsRowSchema>;

/** Top-of-page overall stats across all tests in the window. */
export const tatOverallStatsSchema = z.object({
	order_count: z.number().int(),
	/** Median across order-level TATs (not per-test). */
	median_hours: z.number().nullable(),
	p95_hours: z.number().nullable(),
	/** Count of completed orders where target was set AND exceeded for any test. */
	outlier_count: z.number().int().nullable(),
});
export type TatOverallStats = z.infer<typeof tatOverallStatsSchema>;

export const tatReportResponseSchema = z.object({
	/** The window the server actually used (echoes input or applies defaults). */
	from: z.string().datetime(),
	to: z.string().datetime(),
	overall: tatOverallStatsSchema,
	per_test: z.array(tatStatsRowSchema),
});
export type TatReportResponse = z.infer<typeof tatReportResponseSchema>;
